import { Link as RouterLink } from 'react-router-dom';
import { useTranslations } from '../context/TranslationContext';
import { ArrowRight } from 'lucide-react';
import { getArticles, toArticleLang } from '../services/articles';

const PLACEHOLDER_ARTICLES = [
  { id: 1, color: 'from-[#D4C5B0] to-[#B8A994]' },
  { id: 2, color: 'from-[#C5CFC5] to-[#A8B8A8]' },
  { id: 3, color: 'from-[#C5C8D4] to-[#A8ABB8]' },
];

const CARD_BANNER_COLORS = [
  'from-[#D4C5B0] to-[#B8A994]',
  'from-[#C5CFC5] to-[#A8B8A8]',
  'from-[#C5C8D4] to-[#A8ABB8]',
];

const ArticlesPreview = () => {
  const { t, lang } = useTranslations();
  const articleLang = toArticleLang(lang);
  const strings = t.articlePage;
  const articles = getArticles(articleLang).slice(0, 3);

  return (
    <section className="py-32 bg-white border-t border-hair">
      <div className="max-w-7xl mx-auto px-8 lg:px-16">
        <div className="flex items-end justify-between mb-16" data-reveal>
          <div>
            <div className="eyebrow mb-4">04 / {t.articles.title}</div>
            <h2 className="display text-[42px] md:text-[56px]">{t.articles.title}</h2>
          </div>
          {/* Wrapper carries the responsive hiding: .arrow-link sets its own
              display in index.css, which loads after Tailwind's utilities and
              would otherwise beat `hidden` and show this link on mobile, where
              it collided with the heading. */}
          <div className="hidden md:block">
            <RouterLink to={`/${lang}/articles`} className="arrow-link">
              <span>{t.articles.viewAll}</span>
              <ArrowRight size={16} strokeWidth={1.5} />
            </RouterLink>
          </div>
        </div>

        <div className="flex gap-8 overflow-x-auto pb-4 -mx-8 px-8 lg:mx-0 lg:px-0 snap-x snap-mandatory scrollbar-hide">
          {articles.length > 0
            ? articles.map((article, index) => (
                <RouterLink
                  key={article.slug}
                  to={`/${lang}/articles/${article.slug}`}
                  className="flex-shrink-0 w-[300px] md:w-[calc(33.333%-22px)] snap-start group"
                  data-reveal
                  data-reveal-delay={index === 1 ? '100' : index === 2 ? '200' : undefined}
                >
                  <div
                    className={`h-[50px] bg-gradient-to-r ${CARD_BANNER_COLORS[index % CARD_BANNER_COLORS.length]} mb-6`}
                  />
                  <div className="space-y-3">
                    <h3 className="display text-[22px] leading-[1.2] link-grow">{article.title}</h3>
                    <div className="text-[12px] tracking-[0.1em] uppercase text-mute">
                      {(strings.practiceAreas as Record<string, string>)[article.practiceArea] ?? article.practiceArea}{' '}
                      · {article.readTime} {strings.readTimeSuffix}
                    </div>
                    <p className="text-mute text-sm leading-[1.7] font-light">{article.description}</p>
                  </div>
                </RouterLink>
              ))
            : PLACEHOLDER_ARTICLES.map((article) => (
                <div
                  key={article.id}
                  className="flex-shrink-0 w-[300px] md:w-[calc(33.333%-22px)] snap-start"
                  data-reveal
                  data-reveal-delay={article.id === 2 ? '100' : article.id === 3 ? '200' : undefined}
                >
                  <div className="group cursor-default">
                    <div
                      className={`relative aspect-[4/3] bg-gradient-to-br ${article.color} mb-6 overflow-hidden`}
                    >
                      <div className="absolute inset-0 backdrop-blur-sm bg-white/20 flex items-center justify-center">
                        <span className="eyebrow text-ink/40">{t.articles.comingSoon}</span>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="h-3 bg-ink/[0.06] rounded-full w-24" />
                      <div className="h-5 bg-ink/[0.08] rounded-full w-4/5" />
                      <div className="space-y-2 pt-1">
                        <div className="h-3 bg-ink/[0.05] rounded-full w-full" />
                        <div className="h-3 bg-ink/[0.05] rounded-full w-3/4" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
        </div>

        <div className="mt-12 md:hidden text-center" data-reveal>
          <RouterLink
            to={`/${lang}/articles`}
            className="arrow-link"
          >
            <span>{t.articles.viewAll}</span>
            <ArrowRight size={16} strokeWidth={1.5} />
          </RouterLink>
        </div>
      </div>
    </section>
  );
};

export default ArticlesPreview;
